/**
 * PromoterDashboardPromoters.tsx - Promoter Leaderboard Component
 *
 * This component displays the promoter leaderboard within the promoter dashboard
 * with ranking, performance metrics, and competitive elements.
 *
 * Key Features:
 * - Promoter leaderboard with ranking and performance
 * - Current promoter highlighting and position
 * - Performance metrics for each promoter
 * - Competitive elements and gamification
 * - Progress indicators and achievement status
 * - Responsive design for mobile viewing
 *
 * Leaderboard Information:
 * - Promoter names with avatar/initials
 * - Current ranking position
 * - Total amount raised by each promoter
 * - Goal progress and achievement percentage
 * - Number of donations attributed
 * - Performance trends and indicators
 *
 * Highlighting Features:
 * - Current promoter highlighted in leaderboard
 * - Position indicators and rank badges
 * - Achievement badges and milestones
 * - Performance trend arrows
 * - Goal completion status
 *
 * Competitive Elements:
 * - Ranking positions with visual indicators
 * - Performance comparisons and gaps
 * - Achievement celebrations and recognition
 * - Motivation messaging for improvement
 *
 * Props:
 * - currentPromoterId: string - ID of current promoter for highlighting
 * - showTop?: number - Number of top promoters to display
 *
 * Dependencies: MobX store, ranking utilities, avatar generation
 */

import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Trophy, Star, Target, Gift, Users, UserPlus } from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { PromoterProgressModel } from "../..";

const PromotersSection = styled.div`
  margin: 32px 0;
`;

const ContentCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const SectionTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 32px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LeaderboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LeaderboardItem = styled.div<{ isCurrentPromoter?: boolean }>`
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  background: ${(props) => (props.isCurrentPromoter ? "#f0fdf4" : "#f8fafc")};
  border: ${(props) =>
    props.isCurrentPromoter ? "2px solid #10b981" : "1px solid #e2e8f0"};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const RankBadge = styled.div<{ rank: number; isCurrentPromoter?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-weight: 700;
  font-size: 14px;
  margin-right: 12px;
  flex-shrink: 0;

  ${(props) =>
    props.isCurrentPromoter
      ? `
    background: #10b981;
    color: #ffffff;
  `
      : `
    background: #4b5563;
    color: #ffffff;
  `}
`;

const LeaderAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const LeaderAvatarFallback = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000000;
  font-weight: 700;
  font-size: 12px;
  margin-right: 16px;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const LeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const LeaderName = styled.div<{ isCurrentPromoter?: boolean }>`
  font-size: 16px;
  font-weight: ${(props) => (props.isCurrentPromoter ? "700" : "600")};
  color: ${(props) => (props.isCurrentPromoter ? "#059669" : "#1e293b")};
  margin-bottom: 2px;
  white-space: nowrap;
  margin-right: 16px;
  text-overflow: ellipsis;
`;

const LeaderAmount = styled.div<{ isCurrentPromoter?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.isCurrentPromoter ? "#059669" : "#10b981")};
`;

interface PromoterDashboardPromotersProps {
  promoters: PromoterProgressModel[];
  promoterId: string;
}

export const PromoterDashboardPromoters: React.FC<PromoterDashboardPromotersProps> =
  observer(({ promoters, promoterId }) => {
    const [leaderImageErrors, setLeaderImageErrors] = useState<Set<string>>(
      new Set()
    );

    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    const getInitials = (name: string) => {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase();
    };

    const handleLeaderImageError = (promoterId: string) => {
      setLeaderImageErrors((prev) => {
        const next = new Set(prev);
        next.add(promoterId);
        return next;
      });
    };

    const renderLeaderAvatar = (leader: PromoterProgressModel) => {
      if (leaderImageErrors.has(leader.id)) {
        return (
          <LeaderAvatarFallback>
            {getInitials(leader.displayName)}
          </LeaderAvatarFallback>
        );
      }

      return (
        <LeaderAvatar
          src={leader.imageURL}
          onError={() => handleLeaderImageError(leader.id)}
        />
      );
    };

    const getLeaderboardPromoters = () => {
      if (!promoters?.length) return [];
      const sorted = [...promoters].sort(
        (a, b) => b.totalRaisedAmount - a.totalRaisedAmount
      );

      let currentRank = 1;
      const ranked = sorted.map((p) => {
        if (p.totalRaisedAmount > 0) {
          return new PromoterProgressModel({ ...p, rank: currentRank++ });
        }
        return new PromoterProgressModel({ ...p, rank: undefined });
      });

      const filtered = ranked.filter(
        (p) => p.totalRaisedAmount > 0 || p.id === promoterId
      );

      let top15 = filtered.slice(0, 15);

      const currentInTop15 = top15.some((p) => p.id === promoterId);

      if (!currentInTop15 && promoterId) {
        const currentPromoter = ranked.find((p) => p.id === promoterId);
        if (currentPromoter) {
          top15 = [...top15.slice(0, 14), currentPromoter];
        }
      }

      return top15;
    };

    return (
      <PromotersSection>
        <ContentCard>
          <SectionTitle>
            {/* <img
              src="/user-plus.svg"
              alt="Promoters"
              width={20}
              height={20}
              style={{
                filter:
                  "brightness(0) saturate(100%) invert(47%) sepia(68%) saturate(4717%) hue-rotate(240deg) brightness(91%) contrast(88%)",
              }}
            /> */}
            <UserPlus color="#933dc2" size={20} />
            Promoters
          </SectionTitle>

          <LeaderboardGrid>
            {getLeaderboardPromoters().map((leader) => {
              const isCurrentPromoter = leader.id === promoterId;
              const shouldHideRank = leader.totalRaisedAmount === 0;

              return (
                <LeaderboardItem
                  key={leader.id}
                  isCurrentPromoter={isCurrentPromoter}
                >
                  {renderLeaderAvatar(leader)}

                  <LeaderInfo>
                    <LeaderName isCurrentPromoter={isCurrentPromoter}>
                      {leader.displayName}
                    </LeaderName>
                    <LeaderAmount isCurrentPromoter={isCurrentPromoter}>
                      {formatCurrency(leader.totalRaisedAmount)}
                    </LeaderAmount>
                  </LeaderInfo>

                  {!shouldHideRank && (
                    <RankBadge
                      rank={leader.rank}
                      isCurrentPromoter={isCurrentPromoter}
                    >
                      {leader.rank}
                    </RankBadge>
                  )}
                </LeaderboardItem>
              );
            })}
          </LeaderboardGrid>
        </ContentCard>
      </PromotersSection>
    );
  });
